import React, { useState, useEffect } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "./firebase";
import "./MillionaireGame.css";
import { BlockMath } from "react-katex";
import Latex from "react-latex-next";
import "katex/dist/katex.min.css";
import Confetti from 'react-confetti';
import { useWindowSize } from 'react-use';

const MillionairePlayerView = ({ gameId, playerId, gameData }) => {
  // Wrap a string with LaTeX delimiters if it doesn't already have them
const wrapLatex = (s, mode = "inline") => {
  const t = (s || "").trim();
  if (!t) return "";
  // already delimited? ($...$, $$...$$, \(...\), \[...\])
  if (/^\$[\s\S]*\$$/.test(t) || /^\$\$[\s\S]*\$\$/.test(t) ||
      /^\\\([\s\S]*\\\)$/.test(t) || /^\\\[[\s\S]*\\\]$/.test(t)) return t;
  return mode === "block" ? `$$${t}$$` : `$${t}$`;
};

// Render paragraphs/text/latex for a Millionaire question object
const renderQuestionContent = (q) => {
  if (!q) return null;
  const paragraphs = q.paragraphs || q.questionParagraphs || [];
  const text = q.text || q.questionText || "";
  const latex = q.latex || q.questionLatex || "";
  const optional = q.optionalQuestionText || "";
  const prefix = q.prefix || q.questionPrefix || "";   // 👈 add prefix
  const suffix = q.suffix || q.questionSuffix || "";   // 👈 add suffix

  const isImg = (s) =>
    /^https?:\/\/.+\.(png|jpe?g|webp|gif|svg)$/i.test(s || "");

  if (paragraphs.length > 0) {
    return paragraphs.map((raw, i) => {
      let p = raw;
      if (typeof p === "string") {
        try {
          const maybe = JSON.parse(p);
          if (maybe && (maybe.text || maybe.image)) p = maybe;
        } catch {
          /* leave as string */
        }
      }

      return (
        <div
          key={i}
          style={{ marginBottom: "1rem", lineHeight: 1.6, textAlign: "center" }}
        >
          {typeof p === "string" ? (
            isImg(p) ? (
              <img
                src={p}
                alt={`Q${i + 1}`}
                style={{ maxWidth: "100%", maxHeight: 250 }}
              />
            ) : (
              <Latex>{wrapLatex(p)}</Latex>
            )
          ) : (
            <>
              {p.text && <Latex>{wrapLatex(p.text)}</Latex>}
              {p.image && (
                <img
                  src={p.image}
                  alt={`Q${i + 1}`}
                  style={{
                    maxWidth: "100%",
                    maxHeight: 250,
                    display: "block",
                    marginTop: "0.5rem",
                  }}
                />
              )}
            </>
          )}
        </div>
      );
    });
  }

  return (
    <div style={{ textAlign: "center", lineHeight: 1.6 }}>
      {/* 👇 prefix shown as plain text */}
      {prefix && (
        <div style={{ marginBottom: "0.5rem" }}>{prefix}</div>
      )}

    </div>
  );
};


  const [classSnapshot, setClassSnapshot] = useState([]);
  const normalizeAnswer = (answer) => {
  if (!answer) return "";
  return answer
    .replace(/\\pounds/g, "£")     // Convert LaTeX £
    .replace(/£/g, "")             // Remove all £ symbols
    .replace(/\\frac{(\d+)}{(\d+)}/g, (_, num, denom) => `${num}/${denom}`) // Convert \frac
    .replace(/\\ /g, "")           // Remove backslash spaces
    .replace(/\s+/g, "")           // Remove all spaces
    .trim();
};
  const [selected, setSelected] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [lifelinesUsed, setLifelinesUsed] = useState({
    fiftyFifty: false,
    askClassmate: false,
    askClass: false
  });
  
  const [showAskClassModal, setShowAskClassModal] = useState(false);

const handleAskClass = () => {
  if (submitted || lifelinesUsed.askClass) return;

  const snapshot = Object.values(gameData.players || {}).filter(
    (p) => p.lastAnsweredIndex === currentQuestionIndex && p.answer
  );

  setClassSnapshot(snapshot); // ✅ capture current state
  setShowAskClassModal(true);

  if (snapshot.length > 0) {
    setLifelinesUsed((prev) => ({ ...prev, askClass: true }));
  }
};


  const currentQuestionIndex = gameData.currentQuestionIndex;
  const currentQuestion = gameData.selectedQuestions[currentQuestionIndex - 1];
  const revealed = gameData.revealedQuestionIndex === currentQuestionIndex;
  const answerMap = gameData.players?.[playerId]?.answer || null;
  const [selectedTemp, setSelectedTemp] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [visibleOptions, setVisibleOptions] = useState(currentQuestion.options);
 const handleFiftyFifty = () => {
  if (submitted || lifelinesUsed.fiftyFifty || !currentQuestion) return;

  const allOptions = currentQuestion.options || [];

  // ✅ Filter out invalid entries
  const validOptions = allOptions.filter(
    opt => opt && typeof opt.text === "string"
  );

  // ✅ Normalize correct answer
  const normCorrect = normalizeAnswer(currentQuestion.correctAnswer);

  // ✅ Find correct option
  const correctOption = validOptions.find(
    opt => normalizeAnswer(opt.text) === normCorrect
  );

  if (!correctOption) {
    console.error("❌ Correct option not found:", normCorrect);
    return;
  }

  // ✅ Find all incorrect options
  const incorrectOptions = validOptions.filter(
    opt => normalizeAnswer(opt.text) !== normCorrect
  );

  // ✅ Randomly select TWO to eliminate
  const toRemove = incorrectOptions
    .sort(() => 0.5 - Math.random())
    .slice(0, 2);

  // ✅ Keep correct and one incorrect
  const reducedOptions = validOptions.filter(
    opt => !toRemove.includes(opt)
  );

  setVisibleOptions(reducedOptions);
  setLifelinesUsed(prev => ({ ...prev, fiftyFifty: true }));
};


  
  
  const [showClassmateModal, setShowClassmateModal] = useState(false);
const [classmateAnswer, setClassmateAnswer] = useState(null);
const [selectedClassmate, setSelectedClassmate] = useState(null);
const { width, height } = useWindowSize();
const handleAskClassmate = () => {
    if (submitted || lifelinesUsed.askClassmate) return;
  
    setShowClassmateModal(true);
  };
  
  
  const moneyLadder = [
    "£100", "£200", "£300", "£500", "£1,000",
    "£2,000", "£4,000", "£8,000", "£16,000", "£32,000",
    "£64,000", "£125,000", "£250,000", "£500,000", "⭐£1 MILLION⭐"
  ];

  const handleAnswer = async (optionText) => {
    setSelected(optionText);
    setSubmitted(true);

    await updateDoc(doc(db, "liveGames", gameId), {
        [`players.${playerId}.answer`]: optionText,
        [`players.${playerId}.lastAnsweredIndex`]: currentQuestionIndex,
      });
      
  };
  const handleTakeTheMoney = async () => {
    const amount = moneyLadder[currentQuestionIndex - 1] || "£0";
  
    setGameOver({
      reached: true,
      amount,
      emoji: "💼",
      message: `You took the money and leave with ${amount}!`,
    });
  
    await updateDoc(doc(db, "liveGames", gameId), {
      [`players.${playerId}.finalAmount`]: amount,
      [`players.${playerId}.gameOver`]: true,
    });
  };
  
  useEffect(() => {
    setSelected(null);
    setSubmitted(false);
    setShowConfirmation(false);
    
    // ✅ Force full options immediately
    setVisibleOptions(currentQuestion.options);
  
    // ✅ Optional: temporarily remove animations
    const block = document.querySelector(".question-block");
    if (block) {
      block.classList.remove("animated");
      void block.offsetWidth; // <- force reflow
      block.classList.add("animated");
    }
  }, [currentQuestionIndex]);
  
  

  useEffect(() => {
    if (gameOver?.reached) return; // ✅ Don't reset if game is over
    setSelected(null);
    setSubmitted(false);
  }, [currentQuestionIndex]);
  

  useEffect(() => {
    if (!revealed || !submitted) return;

    const isCorrect = normalizeAnswer(selected) === normalizeAnswer(currentQuestion.correctAnswer);
    if (!isCorrect) {
      const amount = currentQuestionIndex <= 5
        ? "£0"
        : currentQuestionIndex <= 10
        ? "£1,000"
        : "£32,000";

      setTimeout(() => {
        setGameOver({
          reached: true,
          amount,
          message: `You got it wrong! You leave with ${amount}.`,
        });
      }, 1500);
    }
  }, [revealed, submitted, selected, currentQuestion]);
  const confirmFinalAnswer = async () => {
    setShowConfirmation(false);
    setSelected(selectedTemp);
    setSubmitted(true);
  
    await updateDoc(doc(db, "liveGames", gameId), {
        [`players.${playerId}.answer`]: normalizeAnswer(selectedTemp),
        [`players.${playerId}.lastAnsweredIndex`]: currentQuestionIndex,
        [`players.${playerId}.lifelinesUsed.${currentQuestionIndex}.fiftyFifty`]: true,
        [`players.${playerId}.lifelinesUsed.${currentQuestionIndex}.askClassmate`]: true,
        [`players.${playerId}.lifelinesUsed.${currentQuestionIndex}.askClass`]: true,
      });
      
  };
  if (gameOver.reached) {
  

  return (
    <div className="millionaire-wrapper">
      {gameOver.amount === "⭐£1 MILLION⭐" && (
        <>
          <Confetti width={width} height={height} />
          <div
            style={{
              position: "fixed",
              top: "20%",
              width: "100%",
              textAlign: "center",
              fontSize: "3rem",
              color: "#fff",
              fontWeight: "bold",
              zIndex: 9999,
              textShadow: "2px 2px 5px #000"
            }}
          >
            🎉 YOU WIN! 🎉
          </div>
        </>
      )}

      <div className="game-layout">
        <div className="question-block animated fade-in" style={{ textAlign: "center" }}>
          <h2>{gameOver.emoji} Game Over!</h2>
          <p style={{ fontSize: "1.25rem", marginTop: "1rem" }}>{gameOver.message}</p>
        </div>
      </div>
    </div>
  );
}
  

  return (
    
    <div className="millionaire-wrapper">
        <div style={{ textAlign: "center", marginTop: "1.25rem", marginBottom: "0.1rem" }}>
  <button
    className="take-money-btn"
    onClick={handleTakeTheMoney}
    disabled={submitted || revealed}
  >
    💼 Take the Money
  </button>
</div>
        <div className="lifelines">
  <img
    src="https://firebasestorage.googleapis.com/v0/b/ryan-tutors.firebasestorage.app/o/images%2F50-50.gif?alt=media&token=4453e297-63ec-4e3d-9be2-9716a38f580f"
    alt="50:50"
    className={`lifeline-icon-live ${lifelinesUsed.fiftyFifty ? "used" : ""}`}
    onClick={handleFiftyFifty}
  />
  <img
    src="https://firebasestorage.googleapis.com/v0/b/ryan-tutors.firebasestorage.app/o/images%2FAsk%20a%20Classmate.png?alt=media&token=296bf1a7-dfb2-474a-8540-91854577697d"
    alt="Ask a Classmate"
    className={`lifeline-icon-live ${lifelinesUsed.askClassmate ? "used" : ""}`}
    onClick={handleAskClassmate}
  />
  <img
    src="https://firebasestorage.googleapis.com/v0/b/ryan-tutors.firebasestorage.app/o/images%2FAsk%20The%20Audience.gif?alt=media&token=45572f27-6256-47b0-ad60-883b9486542b"
    alt="Ask the Class"
    className={`lifeline-icon-live ${lifelinesUsed.askClass ? "used" : ""}`}
    onClick={handleAskClass}
  />
</div>



         {showConfirmation && (
          <div className="confirmation-modal">
            <div className="confirmation-box">
              <p>🧐 Is that your final answer?</p>
              <div style={{ marginTop: "1rem" }}>
                <button onClick={confirmFinalAnswer} className="start-btn">Yes</button>
                <button
  onClick={() => {
    setShowConfirmation(false);
    setSelectedTemp(null);
  }}
  className="start-btn"
  style={{ marginLeft: "1rem" }}  // ✅ Just spacing
>
  No
</button>

              </div>
            </div>
          </div>
        )}
        {showClassmateModal && (
  <div className="confirmation-modal">
    <div className="confirmation-box">
    {!selectedClassmate ? (
  <>
    <p>👤 Who would you like to ask?</p>

    {Object.entries(gameData.players || {}).filter(
      ([id, player]) =>
        id !== playerId && player.lastAnsweredIndex === currentQuestionIndex
    ).length === 0 ? (
      <>
        <p style={{ marginTop: "1rem" }}>😕 Nobody has answered yet!</p>
        <button
  onClick={() => setShowClassmateModal(false)}
  style={{
    marginTop: "1rem",
    padding: "0.4rem 0.8rem",
    fontSize: "0.85rem",
    backgroundColor: "#ccc",
    color: "#333",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer"
  }}
>
  ← Back to Question
</button>
      </>
    ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
  {Object.entries(gameData.players || {}).map(([id, player]) => {
    const hasAnswered = player.lastAnsweredIndex === currentQuestionIndex;
    if (id === playerId || !hasAnswered) return null;

    return (
      <div
        key={id}
        onClick={() => {
            setSelectedClassmate(player.name || `Player ${id}`);
            setClassmateAnswer(player.answer);
            setLifelinesUsed(prev => ({ ...prev, askClassmate: true }));
          }}
          
        style={{
          background: "linear-gradient(to right, #2d3f91, #3b4db1)",
          borderRadius: "10px",
          padding: "0.75rem 1rem",
          color: "#fff",
          cursor: "pointer",
          fontSize: "1.1rem",
          fontWeight: "600",
          boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
          transition: "transform 0.15s ease",
        }}
        onMouseEnter={e => e.currentTarget.style.transform = "scale(1.03)"}
        onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
      >
        👤 {player.name || `Player ${id}`}
      </div>
    );
  })}
</div>

      
       
    )}
  </>
) : (

        <>
          <p>🧠 {selectedClassmate} chose:</p>
          <h3 style={{ margin: "1rem 0" }}>{classmateAnswer}</h3>
          <div>
            <button
              onClick={async () => {
                setShowClassmateModal(false);
                setSelected(classmateAnswer);
                setSubmitted(true);
                setLifelinesUsed(prev => ({ ...prev, askClassmate: true }));

                await updateDoc(doc(db, "liveGames", gameId), {
                  [`players.${playerId}.answer`]: normalizeAnswer(classmateAnswer),
                  [`players.${playerId}.lastAnsweredIndex`]: currentQuestionIndex,
                });
              }}
              className="start-btn"
            >
              Go with their answer
            </button>
            <button
              onClick={() => {
                setShowClassmateModal(false);
                setSelectedClassmate(null);
              }}
              className="start-btn"
              style={{ marginLeft: "1rem" }}
            >
              Stick with mine
            </button>
          </div>
        </>
      )}
    </div>
  </div>
)}
{showAskClassModal && (
  <div className="confirmation-modal">
    <div className="confirmation-box">
      <p>📊 Here's what your classmates chose:</p>

      {(() => {
        const answers = classSnapshot;


        if (answers.length === 0) {
          return (
            <p style={{ marginTop: "1rem" }}>😕 Nobody has answered yet!</p>
          );
        }

        const tally = {};
answers.forEach((p) => {
  const norm = normalizeAnswer(p.answer);
  tally[norm] = (tally[norm] || 0) + 1;
});


        const total = answers.length;

        return (
            <ul style={{ listStyle: "none", padding: 0, marginTop: "1rem" }}>
            {currentQuestion.options.map((opt, i) => {
              const normOpt = normalizeAnswer(opt.text);
const count = tally[normOpt] || 0;
              const percent = Math.round((count / total) * 100);
          
              return (
                <li
                  key={i}
                  style={{
                    marginBottom: "1rem",
                    background: "linear-gradient(to right, #2d3f91, #3b4db1)",
                    padding: "0.75rem 1rem",
                    borderRadius: "10px",
                    color: "#fff",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                  }}
                >
                  <div
  style={{
    fontWeight: "bold",
    fontSize: "1rem",
    marginBottom: "0.5rem",
  }}
>
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
    <span>
  {opt.text.includes("\\") ? <BlockMath math={opt.text} /> : opt.text}
</span>
    <span style={{ color: "#ffeaa7" }}>{percent}%</span>
  </div>

  <div style={{ marginTop: "px" }}>
    <div
      style={{
        height: "7px",
        width: "100%",
        backgroundColor: "#ffffff66",
        borderRadius: "5px",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: `${percent}%`,
          height: "100%",
          background: "#00cec9",
          transition: "width 0.3s ease-in-out",
        }}
      ></div>
    </div>
  </div>
</div>

                  
                </li>
              );
            })}
          </ul>
          
          
          
        );
      })()}

      <button
        onClick={() => setShowAskClassModal(false)}
        style={{
          marginTop: "1rem",
          padding: "0.4rem 0.8rem",
          fontSize: "0.85rem",
          backgroundColor: "#ccc",
          color: "#333",
          border: "none",
          borderRadius: "6px",
          cursor: "pointer"
        }}
      >
        ← Back to Question
      </button>
    </div>
  </div>
)}

      <div className="game-layout">
        <div className="question-block animated">
  <h2>Question {currentQuestionIndex}</h2>

  {/* 👇 render worded/latex content */}
  <div className="question-content" style={{ marginBottom: "1rem" }}>
    {renderQuestionContent(currentQuestion)}
  </div>

  <div className="options-grid">
            {visibleOptions.map((opt, i) => {
  if (!opt || typeof opt.text !== "string") return null;
  const normOption = normalizeAnswer(opt.text);
  const normCorrect = normalizeAnswer(currentQuestion.correctAnswer);
  const normSelected = normalizeAnswer(selected);
console.log("🧪 Answer submitted. Comparing:");
  console.log("→ Normalized selected:", normSelected);
  console.log("→ Normalized correctAnswer:", normCorrect);
  const isCorrect = normOption === normCorrect;
  const isChosen = normSelected === normOption;

  if (revealed) {
    console.log("🔍 Option:", opt.text);
    console.log("→ Normalized Option:", normOption);
    console.log("→ Normalized Correct:", normCorrect);
    console.log("→ Raw Correct:", currentQuestion.correctAnswer);
    console.log("→ Normalized Selected:", normSelected);
    if (isChosen && !isCorrect) {
      console.warn("⚠️ Selected answer is incorrect when it shouldn't be");
    }
  }



              let className = "option-btn";
              if (revealed) {
                if (isCorrect) {
                  className += " correct";
                } else if (isChosen && !isCorrect) {
                  className += " wrong";
                }
              } else if (isChosen) {
                className += " active-step";
              }

              return (
                <button
                  key={i}
                  className={className}
                  onClick={() => {
                    if (!submitted && !revealed) {
                      setSelectedTemp(opt.text);
                      setShowConfirmation(true);
                    }
                  }}
                  
                  disabled={submitted || revealed}
                >
                  <BlockMath math={opt.text} />


                </button>
              );
            })}
          </div>
        </div>

        <div className="money-ladder">
  {[...moneyLadder].reverse().map((amount, i) => {
    const level = moneyLadder.length - 1 - i;
    const isActive =
      currentQuestionIndex > 1 && level === currentQuestionIndex - 2;
    return (
      <div
        key={i}
        className={`ladder-step ${isActive ? "active-step" : ""}`}
      >
        {amount}
      </div>
    );
  })}
</div>

      </div>
    </div>
  );
};

export default MillionairePlayerView;
